/**
 * @module zarr-layer
 *
 * MapLibre/Mapbox custom layer implementation for rendering Zarr datasets.
 * Implements CustomLayerInterface for direct WebGL rendering.
 */

import type { Readable } from '@zarrita/storage'
import {
  loadDimensionValues,
  getBands,
  toSelectorProps,
  normalizeSelector,
  hashSelector,
} from './zarr-utils'
import { ZarrStore } from './zarr-store'
import { maplibreFragmentShaderSource } from './shaders'
import { ColormapState } from './colormap'
import { ZarrRenderer } from './zarr-renderer'
import type { CustomShaderConfig } from './renderer-types'
import type {
  Bounds,
  ColormapArray,
  SpatialDimensions,
  DimIndicesProps,
  LoadingStateCallback,
  MapLike,
  Selector,
  NormalizedSelector,
  ZarrLayerOptions,
  TransformRequest,
} from './types'
import type { RenderContext } from './renderer-types'
import { RegionRenderer } from './region-renderer'
import {
  computeWorldOffsets,
  resolveProjectionParams,
  isGlobeProjection as checkGlobeProjection,
} from './map-utils'
import { MAPBOX_IDENTITY_MATRIX } from './mapbox-utils'
import type { QueryGeometry, QueryOptions, QueryResult } from './query/types'
import { SPATIAL_DIM_NAMES } from './constants'

type MapboxInternals = {
  transform?: {
    expandedFarZProjMatrix?: Float32Array | Float64Array | number[]
    worldSize?: number
  }
  painter?: {
    transform?: {
      expandedFarZProjMatrix?: Float32Array | Float64Array | number[]
      worldSize?: number
    }
  }
}

/** Scale-mismatch ratio beyond which a locked fixedDataScale is re-derived
 *  from the clim and cached tiles are re-normalized. Conservative: float32
 *  texture data only truly degrades several more orders of magnitude out,
 *  but refetches go through the decoded-chunk cache so an eager threshold
 *  costs little. */
const DATA_SCALE_REFRESH_RATIO = 1e6

/** Extract Mapbox's internal expandedFarZ projection matrix and worldSize.
 *  Falls back per-field: transform may exist without the expanded matrix,
 *  while painter.transform carries it (or vice versa across Mapbox versions). */
function getMapboxGlobeInternals(map: MapLike) {
  const m = map as MapLike & MapboxInternals
  return {
    expandedFarZProjMatrix:
      m.transform?.expandedFarZProjMatrix ??
      m.painter?.transform?.expandedFarZProjMatrix,
    worldSize: m.transform?.worldSize ?? m.painter?.transform?.worldSize,
  }
}

function scaleMercatorMatrix(
  matrix: number[] | Float32Array | Float64Array,
  scale: number
): Float32Array {
  // Mapbox's customLayerMatrix() is the projection matrix scaled from world
  // pixels to normalized Mercator units. To mimic its expanded-far variant,
  // scale the basis columns but leave translation unchanged.
  const out = new Float32Array(matrix)
  for (let i = 0; i < 4; i++) out[i] *= scale
  for (let i = 4; i < 8; i++) out[i] *= scale
  for (let i = 8; i < 12; i++) out[i] *= scale
  return out
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

function mapboxGlobeToMercatorTransition(zoom: number): number {
  // Mirrors Mapbox's current globeToMercatorTransition(zoom) helper.
  return smoothstep(5, 6, zoom)
}

export class ZarrLayer {
  readonly type: 'custom' = 'custom'
  readonly renderingMode: '2d' | '3d'
  readonly wrapTileId = true

  id: string
  private url: string
  private variable: string
  private zarrVersion: 2 | 3 | null = null
  private spatialDimensions: SpatialDimensions
  private bounds: Bounds | undefined
  private crs: string | undefined
  private latIsAscending: boolean | null = null
  private selector: Selector
  private invalidate: () => void

  private colormap: ColormapState
  private clim: [number, number]
  private opacity: number
  private minZoom: number
  private maxZoom: number
  private selectorHash: string = ''

  private _fillValue: number | null = null
  private scaleFactor: number = 1
  private offset: number = 0
  private fixedDataScale: number
  // Once true, fixedDataScale is locked (the renderer has captured it)
  private dataScaleLocked: boolean = false

  private gl: WebGL2RenderingContext | undefined
  private map: MapLike | null = null
  private renderer: ZarrRenderer | null = null
  private regionRenderer: RegionRenderer | null = null
  private tileNeedsRender: boolean = true

  private projectionChangeHandler: (() => void) | null = null
  private resolveGl(
    map: MapLike,
    gl: WebGL2RenderingContext | WebGLRenderingContext | null
  ): WebGL2RenderingContext {
    const isWebGL2 =
      gl &&
      typeof gl.getUniformLocation === 'function' &&
      typeof (gl as WebGL2RenderingContext).drawBuffers === 'function'
    if (isWebGL2) {
      return gl as WebGL2RenderingContext
    }

    const describe = (obj: unknown) =>
      obj
        ? {
            type: obj.constructor?.name,
            keys: Object.keys(obj),
          }
        : null
    console.error('Invalid WebGL2 context passed to onAdd', {
      providedGl: describe(gl),
      painterGl: describe(map?.painter?.context?.gl),
      rendererGl: describe(map?.renderer?.getContext?.()),
    })
    throw new Error('`map` did not provide a valid WebGL2 context')
  }

  private zarrStore: ZarrStore | null = null
  private levelInfos: string[] = []
  private dimIndices: DimIndicesProps = {}
  private dimensionValues: {
    [key: string]: Float64Array | number[] | string[]
  } = {}
  private normalizedSelector: NormalizedSelector = {}
  private isRemoved: boolean = false
  private fragmentShaderSource: string = maplibreFragmentShaderSource
  private customFrag: string | undefined
  private customUniforms: Record<string, number> = {}
  private bandNames: string[] = []
  private customShaderConfig: CustomShaderConfig | null = null
  private onLoadingStateChange: LoadingStateCallback | undefined
  private metadataLoading: boolean = false
  private chunksLoading: boolean = false
  private initError: Error | null = null
  private proj4: string | undefined
  private transformRequest: TransformRequest | undefined
  private customStore: Readable | undefined
  private renderPoles: boolean
  private lastIsGlobe: boolean | null = null
  private usingDirectMapboxGlobePath: boolean = false
  private mapboxDirectGlobePathAvailable: boolean = false

  private canUseMapboxDirectGlobePath(): boolean {
    if (this.mapboxDirectGlobePathAvailable) {
      return true
    }
    if (!this.map || !this.isGlobeProjection()) {
      return false
    }

    // Probe for internal matrix — may not be populated yet during early
    // lifecycle events. Failed probes keep retrying until it appears.
    const { expandedFarZProjMatrix, worldSize } = getMapboxGlobeInternals(
      this.map
    )
    if (expandedFarZProjMatrix && worldSize) {
      this.mapboxDirectGlobePathAvailable = true
      return true
    }
    return false
  }

  private configureMapboxRenderPath(): void {
    if (!this.map || !this.regionRenderer) return

    const resolvedProj4 = this.zarrStore?.proj4 ?? this.proj4
    const resolvedCrs = this.zarrStore?.crs ?? this.crs
    const isEcefEligible = !!resolvedProj4 || resolvedCrs === 'EPSG:4326'
    const isGlobe = this.isGlobeProjection()
    const transition =
      this.map.getZoom && isGlobe
        ? mapboxGlobeToMercatorTransition(this.map.getZoom())
        : 1
    // FRAGILE: Mapbox classifies a custom layer as draped whenever renderToTile
    // exists. We use the direct globe path only for terrain-off, ECEF-eligible
    // datasets at the fully-globe endpoint. During the zoom morph,
    // restore the draped path so Mapbox handles the transition itself with its
    // internal globe/mercator matrices. This means polar coverage can snap back
    // to the draped path near the 5-6 zoom morph window; that is intentional,
    // and is currently more stable than trying to carry the direct ECEF path
    // through Mapbox's custom-layer transition contract.
    // canUseMapboxDirectGlobePath() probes for Mapbox-only internals, so it
    // doubles as the Mapbox discriminator — MapLibre will never pass this gate.
    const shouldUseDirectGlobePath =
      this.renderPoles &&
      isEcefEligible &&
      !this.map.getTerrain?.() &&
      isGlobe &&
      this.canUseMapboxDirectGlobePath() &&
      transition <= 1e-3

    if (shouldUseDirectGlobePath === this.usingDirectMapboxGlobePath) return
    this.usingDirectMapboxGlobePath = shouldUseDirectGlobePath

    if (shouldUseDirectGlobePath) {
      Object.defineProperty(this, 'renderToTile', {
        value: undefined,
        configurable: true,
        writable: true,
      })
      this.map.triggerRepaint?.()
    } else if (Object.prototype.hasOwnProperty.call(this, 'renderToTile')) {
      delete (this as { renderToTile?: unknown }).renderToTile
      // Re-entering the draped path requires fresh tile draws; otherwise Mapbox
      // can show a one-frame gap before renderToTile repopulates tile textures.
      this.tileNeedsRender = true
      this.map.triggerRepaint?.()
    }
  }

  get fillValue(): number | null {
    return this._fillValue
  }

  private isGlobeProjection(): boolean {
    const projection = this.map?.getProjection ? this.map.getProjection() : null
    return checkGlobeProjection(projection)
  }

  /** Check for projection changes and notify mode. Returns current isGlobe state. */
  private syncProjectionState(): boolean {
    const isGlobe = this.isGlobeProjection()
    if (this.lastIsGlobe !== null && this.lastIsGlobe !== isGlobe) {
      this.regionRenderer?.onProjectionChange(isGlobe)
    }
    this.lastIsGlobe = isGlobe
    return isGlobe
  }

  constructor({
    id,
    source,
    variable,
    selector = {},
    colormap,
    clim,
    opacity = 1,
    minzoom = 0,
    maxzoom = Infinity,
    zarrVersion,
    spatialDimensions = {},
    bounds,
    crs,
    latIsAscending = null,
    fillValue,
    customFrag,
    uniforms,
    renderingMode = '3d',
    onLoadingStateChange,
    proj4,
    transformRequest,
    store,
    renderPoles = false,
  }: ZarrLayerOptions) {
    if (!id) {
      throw new Error('[ZarrLayer] id is required')
    }
    if (!source && !store) {
      throw new Error(
        '[ZarrLayer] source is required when store is not provided'
      )
    }
    if (!variable) {
      throw new Error('[ZarrLayer] variable is required')
    }
    if (!colormap || !Array.isArray(colormap) || colormap.length === 0) {
      throw new Error(
        '[ZarrLayer] colormap is required and must be an array of [r, g, b] or hex string values'
      )
    }
    if (!clim || !Array.isArray(clim) || clim.length !== 2) {
      throw new Error('[ZarrLayer] clim is required and must be [min, max]')
    }
    if (proj4 && !bounds) {
      console.warn(
        `[ZarrLayer] proj4 provided without explicit bounds. ` +
          `Bounds will be derived from coordinate arrays if available (see subsequent log for values). ` +
          `For best performance, provide bounds in source CRS units.`
      )
    }

    this.id = id
    this.url = source ?? id // Use id as fallback identifier when using custom store
    this.variable = variable
    this.zarrVersion = zarrVersion ?? null
    this.spatialDimensions = spatialDimensions
    this.bounds = bounds
    this.crs = crs
    this.latIsAscending = latIsAscending ?? null
    this.selector = selector
    this.normalizedSelector = normalizeSelector(selector)
    this.selectorHash = hashSelector(this.normalizedSelector)
    this.renderingMode = renderingMode
    this.invalidate = () => {}
    this.colormap = new ColormapState(colormap)
    this.clim = clim
    this.fixedDataScale = Math.max(Math.abs(clim[0]), Math.abs(clim[1]), 1)
    this.opacity = opacity
    this.minZoom = minzoom
    this.maxZoom = maxzoom

    this.customFrag = customFrag
    this.customUniforms = uniforms || {}

    this.bandNames = getBands(variable, this.normalizedSelector)
    if (this.bandNames.length > 1 || customFrag) {
      this.customShaderConfig = {
        bands: this.bandNames,
        customFrag: customFrag,
        customUniforms: this.customUniforms,
      }
    }

    if (fillValue !== undefined) this._fillValue = fillValue
    this.onLoadingStateChange = onLoadingStateChange
    this.proj4 = proj4
    this.transformRequest = transformRequest
    this.customStore = store
    this.renderPoles = renderPoles
  }

  private emitLoadingState(): void {
    if (!this.onLoadingStateChange) return
    this.onLoadingStateChange({
      loading: this.metadataLoading || this.chunksLoading,
      metadata: this.metadataLoading,
      chunks: this.chunksLoading,
      error: this.initError,
    })
  }

  private handleChunkLoadingChange = (state: {
    loading: boolean
    chunks: boolean
  }): void => {
    this.chunksLoading = state.chunks
    this.emitLoadingState()
  }

  setOpacity(opacity: number) {
    this.opacity = opacity
    this.invalidate()
  }

  setClim(clim: [number, number]) {
    this.clim = clim
    const nextScale = Math.max(Math.abs(clim[0]), Math.abs(clim[1]), 1)
    // Allow fixedDataScale to update until the renderer captures it
    if (!this.dataScaleLocked) {
      this.fixedDataScale = nextScale
    } else if (
      nextScale * DATA_SCALE_REFRESH_RATIO < this.fixedDataScale ||
      this.fixedDataScale * DATA_SCALE_REFRESH_RATIO < nextScale
    ) {
      // Texture data is normalized by fixedDataScale at upload. When the
      // locked scale is orders of magnitude off the new clim (e.g. the layer
      // was created with a dtype-range clim like ±3.4e38 before the caller
      // knew the data range), normalized values underflow float32 and the
      // data can never render correctly again. Adopt the new scale and
      // refetch cached tiles through the decoded-chunk cache so textures
      // are re-normalized.
      this.fixedDataScale = nextScale
      this.regionRenderer?.setDataScale(nextScale)
    }
    this.invalidate()
  }

  setColormap(colormap: ColormapArray) {
    this.colormap.apply(colormap)
    if (this.gl) {
      this.colormap.upload(this.gl)
    }
    this.invalidate()
  }

  setUniforms(uniforms: Record<string, number>) {
    if (!this.customShaderConfig) {
      console.warn(
        '[ZarrLayer] setUniforms() called but layer was not created with customFrag. ' +
          'Uniforms will not be applied. Recreate the layer with customFrag and uniforms options.'
      )
      return
    }
    this.customUniforms = { ...this.customUniforms, ...uniforms }
    this.customShaderConfig.customUniforms = this.customUniforms
    this.invalidate()
  }

  async setVariable(variable: string) {
    if (variable === this.variable) return

    this.metadataLoading = true
    this.emitLoadingState()

    try {
      this.initError = null
      this.variable = variable
      if (this.zarrStore) {
        this.zarrStore.cleanup()
        this.zarrStore = null
      }
      this.dimensionValues = {}
      this._fillValue = null
      // Reset and recompute fixedDataScale from current clim for the new dataset
      this.dataScaleLocked = false
      this.fixedDataScale = Math.max(
        Math.abs(this.clim[0]),
        Math.abs(this.clim[1]),
        1
      )
      await this.initialize()
      await this.initializeRenderer()
      this.invalidate()
    } catch (err) {
      this.initError = err instanceof Error ? err : new Error(String(err))
      console.error('[zarr-layer] Failed to reset:', this.initError.message)
      if (this.regionRenderer && this.gl) {
        this.regionRenderer.dispose(this.gl)
        this.regionRenderer = null
      }
      if (this.zarrStore) {
        this.zarrStore.cleanup()
        this.zarrStore = null
      }
    } finally {
      this.metadataLoading = false
      this.emitLoadingState()
    }
  }

  async setSelector(selector: Selector) {
    const normalized = normalizeSelector(selector)
    const nextHash = hashSelector(normalized)
    if (nextHash === this.selectorHash) {
      return
    }
    this.selectorHash = nextHash
    this.selector = selector
    this.normalizedSelector = normalized

    this.bandNames = getBands(this.variable, this.normalizedSelector)
    if (this.bandNames.length > 1 || this.customFrag) {
      this.customShaderConfig = {
        bands: this.bandNames,
        customFrag: this.customFrag,
        customUniforms: this.customUniforms,
      }
    } else {
      this.customShaderConfig = null
    }

    if (this.regionRenderer) {
      await this.regionRenderer.setSelector(this.normalizedSelector)
    }
    this.invalidate()
  }

  onAdd(
    map: MapLike,
    gl: WebGL2RenderingContext | WebGLRenderingContext
  ): void {
    this._onAddAsync(map, gl)
  }

  private async _onAddAsync(
    map: MapLike,
    gl: WebGL2RenderingContext | WebGLRenderingContext
  ): Promise<void> {
    this.map = map
    const resolvedGl = this.resolveGl(map, gl)
    this.gl = resolvedGl
    this.invalidate = () => {
      this.tileNeedsRender = true
      if (map.triggerRepaint) map.triggerRepaint()
    }

    this.initError = null
    this.metadataLoading = true
    this.emitLoadingState()

    try {
      this.colormap.upload(resolvedGl as WebGL2RenderingContext)
      this.renderer = new ZarrRenderer(
        resolvedGl as WebGL2RenderingContext,
        this.fragmentShaderSource
      )

      this.projectionChangeHandler = () => {
        const isGlobe = this.isGlobeProjection()
        if (this.lastIsGlobe !== isGlobe) {
          this.regionRenderer?.onProjectionChange(isGlobe)
          this.lastIsGlobe = isGlobe
        }
        this.configureMapboxRenderPath()
      }
      if (typeof map.on === 'function' && this.projectionChangeHandler) {
        map.on('projectionchange', this.projectionChangeHandler)
        map.on('style.load', this.projectionChangeHandler)
        map.on('move', this.projectionChangeHandler)
      }

      await this.initialize()
      await this.initializeRenderer()
      this.configureMapboxRenderPath()

      const isGlobe = this.isGlobeProjection()
      this.lastIsGlobe = isGlobe
      this.regionRenderer?.onProjectionChange(isGlobe)

      this.regionRenderer?.update(this.map, this.gl!)
    } catch (err) {
      this.initError = err instanceof Error ? err : new Error(String(err))
      console.error(
        `[zarr-layer] Failed to initialize: ${this.initError.message}. ` +
          `Use onLoadingStateChange callback to handle errors and call map.removeLayer('${this.id}') to clean up.`
      )
      this._disposeResources(resolvedGl)
    } finally {
      this.metadataLoading = false
      this.emitLoadingState()
    }

    if (!this.initError) {
      this.invalidate()
    }
  }

  private async initializeRenderer() {
    if (!this.zarrStore || !this.gl) return

    if (this.regionRenderer) {
      this.regionRenderer.dispose(this.gl)
    }

    // One renderer for every dataset — no per-type branching. A tiled pyramid
    // reaches it as a multiscale whose levels are arrays chunked at the tile
    // size, surfaced as `untiledLevels` by ZarrStore.
    this.regionRenderer = new RegionRenderer(
      this.zarrStore,
      this.variable,
      this.normalizedSelector,
      this.invalidate,
      this.fixedDataScale
    )

    // Lock immediately after the renderer captures the value, before async initialize()
    this.dataScaleLocked = true

    this.regionRenderer.setLoadingCallback(this.handleChunkLoadingChange)
    await this.regionRenderer.initialize()

    if (this.map && this.gl) {
      this.regionRenderer.update(this.map, this.gl)
    }
  }

  private async initialize(): Promise<void> {
    try {
      this.zarrStore = new ZarrStore({
        source: this.url,
        version: this.zarrVersion,
        variable: this.variable,
        spatialDimensions: this.spatialDimensions,
        bounds: this.bounds,
        crs: this.crs,
        latIsAscending: this.latIsAscending,
        coordinateKeys: Object.keys(this.selector),
        proj4: this.proj4,
        transformRequest: this.transformRequest,
        customStore: this.customStore,
      })

      await this.zarrStore.initialized

      const desc = this.zarrStore.describe()

      this.levelInfos = desc.levels
      this.dimIndices = desc.dimIndices
      this.scaleFactor = desc.scaleFactor
      this.offset = desc.addOffset

      if (
        this._fillValue === null &&
        desc.fill_value !== null &&
        desc.fill_value !== undefined
      ) {
        this._fillValue = desc.fill_value
      }

      this.normalizedSelector = normalizeSelector(this.selector)
      await this.loadInitialDimensionValues()

      this.bandNames = getBands(this.variable, this.normalizedSelector)
      if (this.bandNames.length > 1 || this.customFrag) {
        this.customShaderConfig = {
          bands: this.bandNames,
          customFrag: this.customFrag,
          customUniforms: this.customUniforms,
        }
      } else {
        this.customShaderConfig = null
      }
    } catch (err) {
      // Clean up partially-initialized store before re-throwing
      if (this.zarrStore) {
        this.zarrStore.cleanup()
        this.zarrStore = null
      }
      throw err
    }
  }

  private async loadInitialDimensionValues(): Promise<void> {
    if (!this.zarrStore?.root) return

    const multiscaleLevel =
      this.levelInfos.length > 0 ? this.levelInfos[0] : null

    for (const [dimName, value] of Object.entries(this.selector)) {
      this.normalizedSelector[dimName] = toSelectorProps(value)
    }
    for (const dimName of Object.keys(this.dimIndices)) {
      // Skip spatial dimensions - don't load coordinate arrays for these
      if (!SPATIAL_DIM_NAMES.has(dimName.toLowerCase())) {
        try {
          this.dimensionValues[dimName] = await loadDimensionValues(
            this.dimensionValues,
            multiscaleLevel,
            this.dimIndices[dimName],
            this.zarrStore.root,
            this.zarrStore.version
          )

          if (!this.normalizedSelector[dimName]) {
            this.normalizedSelector[dimName] = { selected: 0 }
          }
        } catch (err) {
          console.warn(`Failed to load dimension values for ${dimName}:`, err)
        }
      }
    }
  }

  private isZoomInRange(): boolean {
    if (!this.map?.getZoom) return true
    // In MapLibre globe mode, pole enlargement compensation can push
    // getZoom() negative. Clamp to 0 so that artifact alone doesn't
    // hide the layer.
    const zoom = Math.max(0, this.map.getZoom())
    return zoom >= this.minZoom && zoom <= this.maxZoom
  }

  prerender(
    _gl: WebGL2RenderingContext | WebGLRenderingContext,
    _params: unknown
  ) {
    if (this.isRemoved || !this.gl || !this.regionRenderer || !this.map) return
    if (!this.isZoomInRange()) return

    this.syncProjectionState()
    this.regionRenderer.update(this.map, this.gl)
  }

  render(
    _gl: WebGL2RenderingContext | WebGLRenderingContext,
    params: unknown,
    projection?: { name: string },
    projectionToMercatorMatrix?: number[] | Float32Array | Float64Array,
    projectionToMercatorTransition?: number,
    _centerInMercator?: number[],
    _pixelsPerMeterRatio?: number
  ) {
    if (
      this.isRemoved ||
      !this.renderer ||
      !this.gl ||
      !this.regionRenderer ||
      !this.map
    ) {
      return
    }

    if (!this.isZoomInRange()) {
      return
    }

    this.configureMapboxRenderPath()

    const projectionParams = resolveProjectionParams(
      params,
      projection,
      projectionToMercatorMatrix,
      projectionToMercatorTransition
    )

    if (!projectionParams.matrix) {
      return
    }

    // Legacy MapLibre (no shaderData): fall back to mapbox-style shader path
    // using identity globe-to-merc matrix + transition=1 (pure mercator).
    const legacyMapboxFallback =
      !projectionParams.mapbox && !projectionParams.shaderData
        ? {
            projection: { name: 'mercator' },
            globeToMercatorMatrix: MAPBOX_IDENTITY_MATRIX,
            transition: 1,
          }
        : undefined

    const isGlobe = this.isGlobeProjection()
    const worldOffsets = computeWorldOffsets(this.map, isGlobe)
    const colormapTexture = this.colormap.ensureTexture(this.gl)
    let expandedFarZMercatorMatrix: Float32Array | undefined
    if (projectionParams.mapbox?.projection.name === 'globe') {
      // FRAGILE: Mapbox's internal globe raster pass uses expandedFarZProjMatrix,
      // while customLayerMatrix() uses the regular far plane. Scale the
      // projection matrix by worldSize so the direct ECEF path shares the globe
      // surface's depth behavior.
      const { expandedFarZProjMatrix, worldSize } = getMapboxGlobeInternals(
        this.map
      )
      if (expandedFarZProjMatrix && worldSize) {
        expandedFarZMercatorMatrix = scaleMercatorMatrix(
          expandedFarZProjMatrix,
          worldSize
        )
      }
    }

    const context: RenderContext = {
      gl: this.gl,
      matrix: projectionParams.matrix,
      uniforms: {
        clim: this.clim,
        opacity: this.opacity,
        fillValue: this._fillValue,
        scaleFactor: this.scaleFactor,
        offset: this.offset,
        fixedDataScale: this.fixedDataScale,
      },
      colormapTexture,
      worldOffsets,
      customShaderConfig: this.customShaderConfig || undefined,
      shaderData: projectionParams.shaderData,
      projectionData: projectionParams.projectionData,
      mapbox: projectionParams.mapbox
        ? {
            ...projectionParams.mapbox,
            directGlobePathActive: this.usingDirectMapboxGlobePath,
            expandedFarZMercatorMatrix:
              projectionParams.mapbox.projection.name === 'globe'
                ? expandedFarZMercatorMatrix
                : undefined,
          }
        : legacyMapboxFallback,
    }

    this.regionRenderer.render(this.renderer, context)

    this.tileNeedsRender = false
  }

  renderToTile(
    _gl: WebGL2RenderingContext | WebGLRenderingContext,
    tileId: { z: number; x: number; y: number }
  ) {
    if (
      this.isRemoved ||
      !this.renderer ||
      !this.gl ||
      !this.regionRenderer ||
      !this.map
    ) {
      return
    }

    this.configureMapboxRenderPath()

    const isGlobe = this.syncProjectionState()
    this.regionRenderer.update(this.map, this.gl)

    const colormapTexture = this.colormap.ensureTexture(this.gl)

    const context: RenderContext = {
      gl: this.gl,
      matrix: new Float32Array(16),
      uniforms: {
        clim: this.clim,
        opacity: this.opacity,
        fillValue: this._fillValue,
        scaleFactor: this.scaleFactor,
        offset: this.offset,
        fixedDataScale: this.fixedDataScale,
      },
      colormapTexture,
      worldOffsets: [0],
      customShaderConfig: this.customShaderConfig || undefined,
      isGlobe,
    }

    this.tileNeedsRender = this.regionRenderer.renderToTile(
      this.renderer,
      tileId,
      context
    )
  }

  // Mapbox specific custom layer method required to trigger rerender on eg dataset update.
  shouldRerenderTiles() {
    const needsRender = this.tileNeedsRender
    this.tileNeedsRender = false
    return needsRender
  }

  /**
   * Dispose all GL resources and internal state.
   * Does NOT remove the layer from the map - call map.removeLayer(id) for that.
   */
  private _disposeResources(
    gl: WebGL2RenderingContext | WebGLRenderingContext
  ): void {
    this.isRemoved = true

    this.renderer?.dispose()
    this.renderer = null

    this.colormap.dispose(gl)

    this.regionRenderer?.dispose(gl)
    this.regionRenderer = null

    if (this.zarrStore) {
      this.zarrStore.cleanup()
      this.zarrStore = null
    }

    if (
      this.map &&
      this.projectionChangeHandler &&
      typeof this.map.off === 'function'
    ) {
      this.map.off('projectionchange', this.projectionChangeHandler)
      this.map.off('style.load', this.projectionChangeHandler)
      this.map.off('move', this.projectionChangeHandler)
    }
  }

  onRemove(_map: MapLike, gl: WebGL2RenderingContext | WebGLRenderingContext) {
    const resolvedGl = this.gl ?? this.resolveGl(_map, gl)
    this._disposeResources(resolvedGl)
  }

  // ========== Query Interface ==========

  /**
   * Query all data values within a geographic region.
   * @param geometry - GeoJSON Point, Polygon or MultiPolygon geometry.
   * @param selector - Optional selector to override the layer's selector.
   * @returns Promise resolving to the query result matching carbonplan/maps structure.
   */
  async queryData(
    geometry: QueryGeometry,
    selector?: Selector,
    options?: QueryOptions
  ): Promise<QueryResult> {
    if (!this.regionRenderer) {
      return {
        [this.variable]: [],
        dimensions: [],
        coordinates: {},
      }
    }
    return this.regionRenderer.queryData(geometry, selector, options)
  }
}
